import { Dispatch, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Modal } from '../../../UIPackage/ModalModule/components/Modal';
import { ModalHeader } from '../../../UIPackage/ModalModule/components/ModalHeader';
import { ModalBody } from '../../../UIPackage/ModalModule/components/ModalBody';
import { ModalActions } from '../../../UIPackage/ModalModule/components/ModalActions';
import { Button } from '../../../UIPackage/MainModule/components/Button';
import { FieldBlock } from '../../../UIPackage/FormModule/components/FieldBlock';
import { ISelectOption, SelectInput } from '../../../UIPackage/MainModule/components/SelectInput';
import { TextInput } from '../../../UIPackage/MainModule/components/TextInput';
import { Row } from '../../../UIPackage/FormModule/components/Row';
import { REQUIRED_ERROR } from '../../../CorePackage/MainModule/constants/validationErrors';
import { IPassportGroupApplyRequest } from '../../../APIPackage/PassportGroupsModule/interfaces/IPassportGroupApplyRequest';
import { RootActions } from '../../../CorePackage/MainModule/types/RootActions';
import {
  passportGroupApplyAction,
  passportGroupSetApplySuccessAction,
} from '../../../PassportGroupPackage/MainModule/actions/passportGroupActions';
import { catalogGet } from '../../../CorePackage/CatalogsModule/actions/catalogsActions';
import { CommonCatalogsNamesEnum } from '../../../CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { getPassportMassCancelReasonSelectOptions } from '../../../CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { passportGroupApplySuccessSelector } from '../../../PassportGroupPackage/MainModule/selectors/passportGroupSelector';
import { Checkbox } from '../../../UIPackage/MainModule/components/Checkbox';
import { IKnmAttachment } from '../../../APIPackage/KnmModule/interfaces/IKnmAttachment';
import { PassportGroupFileInput } from './PassportGroupFileInput';
import { IPassportGroupReason } from '../../../APIPackage/PassportGroupsModule/interfaces/IPassportGroupReason';
import { InfoBlock } from '../../../CorePackage/MainModule/components/InfoBlock';
import { addNoticeAction } from '../../../CorePackage/NotificationModule/actions/NoticeActions';
import { NoticeTypesEnum } from '../../../CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { MessageTypesEnum } from '../../../CorePackage/NotificationModule/enums/MessageTypesEnum';

interface IPassportMassCancellationModalProps {
  groupId: number;
  onClose: () => void;
  allowKno?: boolean;
}

const newReasonSelectOption: ISelectOption = {
  id: 'new',
  label: 'Создать новое',
};

export const PassportMassCancellationModal = ({ groupId, onClose, allowKno }: IPassportMassCancellationModalProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const massCancelReasonSelectOptions = useSelector(getPassportMassCancelReasonSelectOptions);
  const isMassCancelSuccess = useSelector(passportGroupApplySuccessSelector);
  const [reasonNewValue, setReasonNewValue] = useState<string>('');
  const [reasonValue, setReasonValue] = useState<ISelectOption>();
  const [reasonNewError, setReasonNewError] = useState<string>();
  const [reasonError, setReasonError] = useState<string>();
  const [isShowErrors, setIsShowErrors] = useState(false);
  const [isDecisionRequired, setIsDecisionRequired] = useState(false);
  const [isBlock, setIsBlock] = useState(false);
  const [attachment, setAttachment] = useState<IKnmAttachment>();

  useEffect(() => {
    dispatch(catalogGet(CommonCatalogsNamesEnum.PASSPORT_MASS_CANCEL_REASON, true));
  }, [dispatch]);

  useEffect(() => {
    setReasonNewValue('');
  }, [reasonValue, setReasonNewValue]);

  useEffect(() => {
    setReasonError(!reasonValue ? REQUIRED_ERROR : undefined);
    setReasonNewError(reasonValue?.id === 'new' && !reasonNewValue ? REQUIRED_ERROR : undefined);
  }, [reasonValue, reasonNewValue, setReasonError, setReasonNewError]);

  useEffect(() => {
    if (isMassCancelSuccess) {
      dispatch(passportGroupSetApplySuccessAction(false));
      dispatch(
        addNoticeAction({
          renderContent: 'Запущен фоновый процесс отмены мероприятий группы',
          noticeType: NoticeTypesEnum.SUCCESS,
          messageType: MessageTypesEnum.PASSPORT_GROUP,
        }),
      );
      onClose();
    }
  }, [dispatch, isMassCancelSuccess, onClose]);

  const handleSubmit = useCallback(() => {
    setIsShowErrors(true);

    if (reasonError || reasonNewError) {
      return;
    }

    const reason: IPassportGroupReason =
      typeof reasonValue?.id === 'number'
        ? { id: reasonValue.id, name: reasonValue.label }
        : { id: null, name: reasonNewValue };

    const data: IPassportGroupApplyRequest = {
      reason,
      reasonFile: attachment || null,
      isAllow: allowKno || false,
      isDecisionRequired,
      isBlock,
    };

    dispatch(passportGroupApplyAction(groupId, data));
  }, [
    setIsShowErrors,
    reasonError,
    reasonNewError,
    reasonValue,
    reasonNewValue,
    groupId,
    attachment,
    allowKno,
    isDecisionRequired,
    isBlock,
  ]);

  return (
    <Modal visible onClose={onClose}>
      <ModalHeader title={allowKno ? 'Предоставление возможности отмены мероприятий' : 'Массовая отмена мероприятий'} />
      <ModalBody size="content">
        <InfoBlock iconVerticalPosition="top" marginBottom="1.25rem">
          {allowKno ? (
            <>
              Внимание! КНО–владельцы мероприятий получат возможность отменить данные мероприятия. Если Вы уверены, что
              хотите продолжить, укажите основание массовой отмены и нажмите «Подтвердить». Если Вы не хотите
              предоставлять отмену мероприятий контрольным (надзорным) органам, нажмите «Назад»
            </>
          ) : (
            <>
              Внимание! Мероприятия будут переведены в статус «Отменено». Если Вы уверены, что хотите продолжить,
              укажите основание массовой отмены, прикрепите файл основания и нажмите «Подтвердить». Если Вы не хотите
              отменять мероприятия, нажмите «Назад»
            </>
          )}
        </InfoBlock>
        <Row>
          <FieldBlock label="Основание массовой отмены">
            <SelectInput
              isSearchable
              onChange={setReasonValue}
              value={reasonValue}
              options={[newReasonSelectOption, ...massCancelReasonSelectOptions]}
              error={reasonError}
              invalid={isShowErrors && !!reasonError}
            />
          </FieldBlock>
        </Row>
        {reasonValue?.id === 'new' && (
          <Row>
            <FieldBlock>
              <TextInput
                value={reasonNewValue}
                onChange={e => setReasonNewValue(e.target.value)}
                error={reasonNewError}
                invalid={isShowErrors && !!reasonNewError}
              />
            </FieldBlock>
          </Row>
        )}
        <PassportGroupFileInput
          label="Файл основания массовой отмены"
          setAttachment={setAttachment}
          attachment={attachment}
        />
        {allowKno && (
          <>
            <Row>
              <Checkbox
                id="decisionRequiredField"
                theme="system"
                label="Требовать вынесения решения при отмене контрольным (надзорным) органом"
                checked={isDecisionRequired}
                onChange={e => setIsDecisionRequired(e.target.checked)}
              />
            </Row>
            <Row>
              <Checkbox
                id="isBlockField"
                theme="system"
                label="Блокировать проведение мероприятий"
                checked={isBlock}
                onChange={e => setIsBlock(e.target.checked)}
              />
            </Row>
          </>
        )}
      </ModalBody>
      <ModalActions>
        <Button text="Подтвердить" onClick={handleSubmit} size="medium" color="primary" />
        <Button text="Назад" onClick={onClose} size="medium" />
      </ModalActions>
    </Modal>
  );
};
